import { ApolloError } from '@apollo/client';
import {
  Box,
  Circle,
  Flex,
  HStack,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { Panel, PanelHeader, PanelHeading } from '@gamma/ui/layout';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import {
  graphqlErrorRedirects,
  MITRE_COLORS_PRIORITY_RANGE,
} from '@investigator/shared/constants';
import {
  useDateRangeQueryString,
  useUserSession,
} from '@investigator/shared/hooks';
import { i18n } from '@investigator/shared/localization';
import { IGetDetectionMitreSummary } from '../../../detections/queries';

import { DetectionsByCategoryPopoverContent } from './DetectionsByCategoryPopoverContent';
import HelpOutlinedIcon from '@gamma/ui/icons/HelpOutlinedIcon';

interface DetectionsByCategoryPanelProps {
  loading: boolean;
  error?: ApolloError;
  data?: IGetDetectionMitreSummary;
}

export function DetectionsByCategoryPanel({
  loading,
  error,
  data,
}: DetectionsByCategoryPanelProps) {
  const { signOut } = useUserSession();

  const { start, end } = useDateRangeQueryString();

  if (loading || error) {
    return (
      <Panel>
        <GraphQLReqStatus
          loading={loading}
          error={error}
          signOut={signOut}
          extended={graphqlErrorRedirects}
        />
      </Panel>
    );
  }

  const alertMitreSummary = data?.getDetectionMitreSummary || [];

  const { includesOpen } = i18n.pages.securityOverview;
  const { heading } = i18n.pages.securityOverview.panels.detectionsByCategory;

  const tacticSize = alertMitreSummary.length > 6 && { fontSize: '13px' };

  return (
    <Panel
      h="100%"
      minH={0}
      flex="1 1 0"
      display="flex"
      flexDir="column"
      overflow="hidden"
    >
      <PanelHeader divider flexShrink={0}>
        <PanelHeading>
          <HStack>
            <Text>{heading}</Text>
            <Tooltip label={includesOpen} shouldWrapChildren>
              <HelpOutlinedIcon
                color="text.secondary"
                data-testid="mitre-attack-map-info-label"
              />
            </Tooltip>
          </HStack>
        </PanelHeading>
      </PanelHeader>
      <Flex flex="1 1 0" minH={0} overflow="auto" direction="column">
        <Flex mb={5} wrap="wrap" flexShrink={0}>
          {alertMitreSummary.length
            ? alertMitreSummary.map((tactic, index) => (
                <Flex alignItems="center" mb={0.5} key={index}>
                  <Circle
                    mr={3}
                    size={2}
                    data-testid="mitre-category-key-item"
                    bg={
                      MITRE_COLORS_PRIORITY_RANGE[
                        tactic.mitre_attack_tactic_priority
                      ]
                    }
                  />
                  <Text mr={3} textStyle="body-md">
                    {tactic.mitre_attack_tactic}
                  </Text>
                </Flex>
              ))
            : i18n.pages.securityOverview.na}
        </Flex>
        <Flex alignItems="flex-start" flex="1 1 0" minH={0} gap={0.5}>
          {alertMitreSummary.map((tactic, index) => {
            return (
              <Flex
                flex={1}
                minW={0}
                gap={0.5}
                key={index}
                overflow="hidden"
                direction="column"
                data-testid="mitre-tactic"
                maxW={`${100 / alertMitreSummary.length}%`}
              >
                {tactic.mitre_attack_techniques.map(
                  (technique, index: number) => (
                    <Popover
                      isLazy
                      key={index}
                      trigger="hover"
                      openDelay={300}
                      placement={'right'}
                    >
                      <PopoverTrigger>
                        <Flex
                          flex="auto"
                          minW={0}
                          w="95%"
                          key={index}
                          color="gray.50"
                          borderRadius="sm"
                          direction="column"
                          overflow="hidden"
                          position="relative"
                          justifyContent="space-between"
                          data-testid="mitre-technique"
                          border="1px solid rgba(255,255,255, 0.2)"
                          bg={
                            MITRE_COLORS_PRIORITY_RANGE[
                              tactic.mitre_attack_tactic_priority
                            ]
                          }
                          mr={index + 1 !== alertMitreSummary.length ? 2 : 0}
                          h={
                            technique.entity_count > 5
                              ? `${
                                  62 +
                                  (technique.entity_count <= 100
                                    ? technique.entity_count
                                    : 100) *
                                    1.5
                                }px`
                              : '62px'
                          }
                        >
                          <Flex
                            p={2}
                            flex="auto"
                            direction="column"
                            justifyContent="space-between"
                            bg={
                              MITRE_COLORS_PRIORITY_RANGE[
                                tactic.mitre_attack_tactic_priority
                              ]
                            }
                          >
                            <Text
                              noOfLines={2}
                              textStyle="body-md"
                              paddingRight={4}
                              position="absolute"
                              w="calc(100% - 16px)"
                              data-testid="technique-entity-name"
                              {...tacticSize}
                            >
                              {technique.mitre_attack_technique}
                            </Text>
                            <Flex>
                              <Box
                                px={0.5}
                                py={1}
                                right={2}
                                bottom={2}
                                minW={'15px'}
                                textAlign="center"
                                borderRadius="base"
                                position="absolute"
                                bg="rgba(255, 255, 255, 0.2)"
                              >
                                <Text
                                  data-testid="technique_entity_count"
                                  textStyle="body-md"
                                >
                                  {technique.entity_count}
                                </Text>
                              </Box>
                            </Flex>
                          </Flex>
                        </Flex>
                      </PopoverTrigger>
                      <PopoverContent
                        w="450px"
                        data-testid="mitre-technique-popover-content"
                      >
                        <DetectionsByCategoryPopoverContent
                          start={start}
                          end={end}
                          tactic={tactic}
                          technique={technique}
                        />
                      </PopoverContent>
                    </Popover>
                  ),
                )}
              </Flex>
            );
          })}
        </Flex>
      </Flex>
    </Panel>
  );
}
